import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import { Plus, Edit, Power, PowerOff } from "lucide-react";

const EMPTY = {
    code: "",
    title: "",
    description: "",
    type: "flat",
    value: 50,
    min_order_amount: 0,
    max_discount: 200,
    max_uses_total: null,
    max_uses_per_user: 1,
    valid_until: "",
    active: true,
    allowed_user_segments: [],
};

const AdminPromos = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/promos").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("__new__"); setDraft(EMPTY); };
    const openEdit = (p) => { setEditing(p.id); setDraft({ ...EMPTY, ...p }); };

    const save = async () => {
        setSaving(true);
        try {
            if (editing === "__new__") {
                await api.post("/admin/promos", draft);
            } else {
                const patch = { ...draft };
                delete patch.id; delete patch.created_at; delete patch.updated_at; delete patch.uses;
                await api.patch(`/admin/promos/${editing}`, patch);
            }
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (p) => {
        try {
            await api.patch(`/admin/promos/${p.id}`, { active: !p.active });
            toast.success(`${p.code} ${p.active ? "disabled" : "enabled"}`);
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <AdminAuthGuard>
            <AdminLayout>
                <div className="mb-6 flex items-end justify-between gap-3">
                    <div>
                        <p className="label-uppercase text-zinc-500">Catalog</p>
                        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Promo codes</h1>
                    </div>
                    <button onClick={openNew} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800" data-testid="admin-promo-new">
                        <Plus className="h-4 w-4" /> New code
                    </button>
                </div>

                {loading ? <p className="text-sm text-zinc-500">Loading…</p> : (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white dark:border-white/10 dark:bg-white/[0.03]">
                        <table className="min-w-full text-sm" data-testid="admin-promos-table">
                            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-white/5">
                                <tr>
                                    <th className="px-4 py-2">Code</th>
                                    <th className="px-4 py-2">Title</th>
                                    <th className="px-4 py-2">Type</th>
                                    <th className="px-4 py-2">Value</th>
                                    <th className="px-4 py-2">Min order</th>
                                    <th className="px-4 py-2">Uses</th>
                                    <th className="px-4 py-2">Status</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} className="border-t border-zinc-100 dark:border-white/5">
                                        <td className="px-4 py-2 font-mono font-medium">{p.code}</td>
                                        <td className="px-4 py-2">{p.title}</td>
                                        <td className="px-4 py-2 text-xs">{p.type}</td>
                                        <td className="px-4 py-2">{p.type === "percent" ? `${p.value}%` : p.type === "flat" ? `₹${p.value}` : "—"}</td>
                                        <td className="px-4 py-2">{p.min_order_amount ? `₹${p.min_order_amount}` : "—"}</td>
                                        <td className="px-4 py-2 text-xs">{p.uses ?? 0}{p.max_uses_total ? ` / ${p.max_uses_total}` : ""}</td>
                                        <td className="px-4 py-2">
                                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                                {p.active ? "Active" : "Disabled"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => openEdit(p)} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-white/10" title="Edit"><Edit className="h-4 w-4" /></button>
                                                <button onClick={() => toggle(p)} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-white/10" title={p.active ? "Disable" : "Enable"}>
                                                    {p.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {editing && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
                        <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-zinc-900" data-testid="admin-promo-modal">
                            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{editing === "__new__" ? "New promo" : `Edit ${draft.code}`}</h2>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Code</span><input disabled={editing !== "__new__"} value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5 font-mono uppercase tracking-wider" /></label>
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Title</span><input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Description</span><input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label><span className="text-xs text-zinc-500">Type</span>
                                    <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5">
                                        <option value="flat">Flat ₹</option>
                                        <option value="percent">Percent %</option>
                                        <option value="free_shipping">Free shipping</option>
                                    </select>
                                </label>
                                <label><span className="text-xs text-zinc-500">Value</span><input type="number" value={draft.value} onChange={(e) => setDraft({ ...draft, value: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label><span className="text-xs text-zinc-500">Min order</span><input type="number" value={draft.min_order_amount} onChange={(e) => setDraft({ ...draft, min_order_amount: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label><span className="text-xs text-zinc-500">Max discount</span><input type="number" value={draft.max_discount ?? ""} onChange={(e) => setDraft({ ...draft, max_discount: e.target.value ? Number(e.target.value) : null })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label><span className="text-xs text-zinc-500">Max uses (total)</span><input type="number" value={draft.max_uses_total ?? ""} onChange={(e) => setDraft({ ...draft, max_uses_total: e.target.value ? Number(e.target.value) : null })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label><span className="text-xs text-zinc-500">Max uses (per user)</span><input type="number" value={draft.max_uses_per_user ?? 1} onChange={(e) => setDraft({ ...draft, max_uses_per_user: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" /></label>
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Segments (comma-separated: new_user, elite_member)</span>
                                    <input
                                        value={(draft.allowed_user_segments || []).join(", ")}
                                        onChange={(e) => setDraft({ ...draft, allowed_user_segments: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                                        className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5"
                                    />
                                </label>
                            </div>
                            <div className="mt-5 flex items-center justify-end gap-2">
                                <button onClick={() => setEditing(null)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm hover:bg-zinc-50" data-testid="admin-promo-modal-cancel">Cancel</button>
                                <button onClick={save} disabled={saving} className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-promo-modal-save">{saving ? "Saving…" : "Save"}</button>
                            </div>
                        </div>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminPromos;
